import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import './modal.css';
import { classificarMensagem } from '../../utils/classificarMensagem';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_823 || '',
  auth: { username: 'Portal', password: 'Portal1163' },
});

function formatarData(dataInput) {
  return new Date(dataInput).toLocaleString('pt-BR', { timeZone: 'UTC' });
}

function capitalizeFirstLetter(str) {
  return str.toLowerCase().replace(/(^|\s)\S/g, (match) => match.toUpperCase());
}

const ListaRegistrosDev = ({ filtroNivel }) => {
  const [state, setState] = useState({
    registrosOriginais: [],
    registros: [],
    erro: false,
    modalIsOpen: false,
    modalData: [],
    filtroProjeto: ' Todos',
    projetos: [],
    exclusaoEmAndamento: false,
  });

  const carregarDadosIniciais = useCallback(async () => {
    try {
      const [devResponse, prodResponse] = await Promise.all([
        axiosInstance.get('/dev_exception'),
        axiosInstance.get('/pdv_exception/false'),
      ]);
      const devRecords = devResponse.data.map((r) => ({ ...r, _source: 'dev' }));
      const testeRecords = prodResponse.data
        .filter((r) => r.cnpj === '11111111111111')
        .map((r) => ({ ...r, _source: 'prod' }));
      const registrosOriginais = [...devRecords, ...testeRecords];
      const projetosUnicos = Array.from(new Set(registrosOriginais.map((r) => r.projeto)))
        .filter(Boolean);
      setState((prev) => ({
        ...prev,
        registrosOriginais,
        projetos: [' Todos', ...projetosUnicos].sort(),
      }));
    } catch {
      setState((prev) => ({ ...prev, erro: true }));
    }
  }, []);

  const filtrarPorProjeto = useCallback((projeto) => {
    setState((prev) => ({
      ...prev,
      registros: projeto === ' Todos'
        ? prev.registrosOriginais
        : prev.registrosOriginais.filter((r) => r.projeto === projeto),
      filtroProjeto: projeto,
    }));
  }, []);

  const excluirRegistro = useCallback(async (id, source) => {
    if (source === 'prod') {
      await axiosInstance.post(`/pdv_exception/${id}`, '');
    } else {
      await axiosInstance.get(`/dev_exception_del/${id}`);
    }
  }, []);

  const limparRegistros = useCallback(async (records) => {
    const ids = records.map((r) => r.controle);
    setState((prev) => ({ ...prev, exclusaoEmAndamento: true }));
    try {
      for (const record of records) {
        await excluirRegistro(record.controle, record._source);
      }
      setState((prev) => ({
        ...prev,
        registrosOriginais: prev.registrosOriginais.filter((r) => !ids.includes(r.controle)),
        exclusaoEmAndamento: false,
      }));
    } catch {
      setState((prev) => ({ ...prev, exclusaoEmAndamento: false }));
    }
  }, [excluirRegistro]);

  const exibirDetalhes = useCallback((records) => {
    setState((prev) => ({ ...prev, modalData: records, modalIsOpen: true }));
  }, []);

  const closeModal = useCallback(() => {
    setState((prev) => ({ ...prev, modalIsOpen: false }));
    carregarDadosIniciais();
  }, [carregarDadosIniciais]);

  useEffect(() => {
    carregarDadosIniciais();
    const interval = setInterval(carregarDadosIniciais, 10000);
    return () => clearInterval(interval);
  }, [carregarDadosIniciais]);

  useEffect(() => {
    filtrarPorProjeto(state.filtroProjeto);
  }, [state.registrosOriginais, state.filtroProjeto, filtrarPorProjeto]);

  const groupByCnpj = useCallback((records) => {
    const grouped = records.reduce((acc, r) => {
      acc[r.cnpj] = (acc[r.cnpj] || []).concat(r);
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([cnpj, recs]) => {
        const classificacoes = recs.map((r) => classificarMensagem(r.mensagem));
        const classificacao = classificacoes.reduce((mais, atual) =>
          atual.ordem < mais.ordem ? atual : mais
        );
        return { cnpj, records: recs, count: recs.length, classificacao };
      })
      .sort((a, b) => {
        if (a.classificacao.ordem !== b.classificacao.ordem)
          return a.classificacao.ordem - b.classificacao.ordem;
        return new Date(b.records[0].recepcao) - new Date(a.records[0].recepcao);
      });
  }, []);

  const sortedRegistros = groupByCnpj(state.registros);
  const visibleRegistros = filtroNivel
    ? sortedRegistros.filter(g => g.classificacao.nivel === filtroNivel)
    : sortedRegistros;

  return (
    <div>
      {state.erro ? (
        <div className="alert alert-danger">Erro ao carregar dados do servidor. Por favor, tente novamente mais tarde.</div>
      ) : (
        <React.Fragment>
          <p className="text-muted small mb-2">Erros disparados em desenvolvimento / teste</p>

          <div className="d-flex flex-wrap gap-2 mb-3">
            {state.projetos.map((projeto, index) => {
              const records = projeto === ' Todos'
                ? state.registrosOriginais
                : state.registrosOriginais.filter((r) => r.projeto === projeto);
              const sevs = records.map((r) => classificarMensagem(r.mensagem));
              const piorOrdem = sevs.length > 0 ? Math.min(...sevs.map((s) => s.ordem)) : 99;
              const pior = sevs.find((s) => s.ordem === piorOrdem);
              const badgeClass = pior ? pior.badgeClass : 'bg-secondary';
              const isActive = state.filtroProjeto === projeto;
              return (
                <button
                  key={index}
                  type="button"
                  className={`btn btn-sm ${isActive ? 'btn-dark' : 'btn-outline-secondary'}`}
                  onClick={() => filtrarPorProjeto(projeto)}
                >
                  {capitalizeFirstLetter(projeto)}
                  <span className={`badge ${badgeClass} ms-2`}>{records.length}</span>
                </button>
              );
            })}
          </div>

          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
            {visibleRegistros.length > 0 ? (
              visibleRegistros.map(({ cnpj, records, count, classificacao }) => (
                <div className="col" key={cnpj}>
                  <div className="card h-100 shadow-sm">
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5 className="card-title mb-0">{records[0].nome}</h5>
                        <span className={`badge ${classificacao.badgeClass} rounded-pill fs-6`}>{count}</span>
                      </div>
                      <h6 className="card-subtitle mb-2 text-muted">{cnpj}</h6>
                      <p className="card-text text-sm mb-2">
                        <small className="text-muted">{records[0].classe} - {records[0].projeto}</small>
                      </p>
                      <div className={`alert ${classificacao.alertClass} p-2 mb-3`} role="alert">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <strong>Mensagem:</strong>
                          <span className={`badge ${classificacao.badgeClass} ms-2`}>
                            {classificacao.tipo} — {classificacao.nivel}
                          </span>
                        </div>
                        <div dangerouslySetInnerHTML={{ __html: records[0].mensagem }} />
                      </div>
                      <div className="mt-auto d-flex justify-content-end gap-2">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => exibirDetalhes(records)}>
                          Detalhes
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          disabled={state.exclusaoEmAndamento}
                          onClick={() => limparRegistros(records)}
                        >
                          {state.exclusaoEmAndamento ? 'Limpando...' : 'Limpar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col">
                <div className="alert alert-info" role="alert">
                  Nenhum registro encontrado para o filtro selecionado.
                </div>
              </div>
            )}
          </div>
        </React.Fragment>
      )}

      <Modal
        isOpen={state.modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Detalhes"
        className="react-modal-content"
        overlayClassName="react-modal-overlay"
      >
        <div className="modal-header modal-header-wrapper">
          <h5 className="modal-title">Detalhes dos Registros</h5>
          <button type="button" className="btn-close" aria-label="Fechar" onClick={closeModal} />
        </div>
        {state.modalData.length > 0 ? (
          <div className="modal-table">
            <table className="table table-hover font-sm data-table">
              <thead className="table-header">
                <tr>
                  <th>Classe/ID</th>
                  <th>Erro</th>
                </tr>
              </thead>
              <tbody>
                {state.modalData.map((registro, index) => (
                  <tr key={index}>
                    <td className="limited-cell">
                      <p className="fw-bold mb-0">{registro.classe}</p>
                      <small className="text-muted text-nowrap">{formatarData(registro.recepcao)}</small>
                    </td>
                    <td style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: registro.mensagem }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Nenhum registro encontrado.</p>
        )}
      </Modal>
    </div>
  );
};

export default ListaRegistrosDev;
